/**
 * D'OÙ VIENT UNE VALEUR (DEV-1508 lot 2e, décision de Mathieu du 11/09/2026).
 * ============================================================================
 *
 * INTENTION : celui qui regarde sa courbe doit savoir, sans rien demander, si ce
 * qu'il lit a été MESURÉ par son compteur, CALCULÉ à coup sûr, seulement ESTIMÉ,
 * ou CORRIGÉ après coup. Quatre mots, et rien entre les deux.
 *
 * Trois règles, toutes tenues ici et nulle part ailleurs.
 *
 * 1. L'ORIGINE SE LIT POINT PAR POINT. La provenance servie avec chaque point
 *    (`t[i]`) est le seul juge. Un drapeau posé sur la série entière ne suffit
 *    plus jamais : sur un compteur réel, la moyenne horaire arrivait calculée
 *    point par point et s'affichait pourtant « Mesuré », parce que la série ne
 *    portait aucun drapeau de calcul.
 *
 * 2. UN CALCUL N'EST CERTAIN QUE S'IL EST COMPLET. Un point calculé sur tous les
 *    pas qu'il devait agréger est « Calculé » ; le même point calculé sur une
 *    partie d'entre eux est « Estimé ». C'est la différence entre une somme et
 *    une extrapolation, et elle se voit à l'écran.
 *
 * 3. RIEN NE SE DÉDUIT D'UN FAIT NON SERVI (charte art. 3). Un compte absent
 *    veut dire « je n'ai pas lu », jamais « couverture incomplète » : sans lui,
 *    un calcul reste un calcul. Et un pas non collecté n'a AUCUNE origine — il
 *    ne pèse dans aucune part, exactement comme il ne vaut jamais 0.
 *
 * Les MOTS eux-mêmes vivent dans `ongletsEnedis.ts` (`LIBELLES_ORIGINE`), où la
 * garde de vocabulaire les relit.
 */
import type {
    GrainSeries,
    ProvenanceSeries,
} from "@modules/data/services/sourceDataEnedis.service"
import {
    LIBELLES_ORIGINE,
    type OrigineSerie,
} from "@modules/data/utils/ongletsEnedis"

/** La part d'une origine dans une série, telle qu'elle s'écrit à côté d'elle. */
export type PartOrigine = {
    origine: OrigineSerie
    /** Points portant cette origine. */
    points: number
    /** Part arrondie à l'entier, sur les points DONT ON CONNAÎT l'origine. */
    pourcent: number
}

export type RepartitionOrigine = {
    /** L'origine de chaque point, alignée sur `x`. `null` = pas non collecté. */
    parPoint: Array<OrigineSerie | null>
    /** Points dont l'origine est connue. Les autres ne pèsent nulle part. */
    pointsConnus: number
    /** Les origines présentes, dans l'ordre de lecture, avec leur part. */
    parts: PartOrigine[]
    /** L'origine de la série quand elle est homogène, sinon `null`. */
    homogene: OrigineSerie | null
}

/** L'ordre de lecture des origines : du plus sûr au plus lointain de la mesure. */
export const ORDRE_ORIGINES: OrigineSerie[] = [
    "mesure",
    "calcul",
    "estimation",
    "correction",
]

/**
 * Ce qu'un point agrégé DEVAIT agréger, quand on peut le savoir.
 *
 * À l'heure, c'est le nombre de pas du compteur dans une heure : le pas est un
 * fait servi par le référentiel du point, pas une hypothèse. Au pas du compteur,
 * un point vaut un point. Partout ailleurs (jour, semaine, mois, période), le
 * lecteur agrège depuis ses propres feuilles et l'attendu ne se déduit PAS du
 * pas du compteur : on rend `null` plutôt qu'un nombre plausible, qui ferait
 * passer des calculs complets pour des estimations.
 */
export function attenduParPoint(
    grain: GrainSeries,
    minutesDuPasCompteur: number | null | undefined
): number | null {
    if (grain === "natif") return 1
    if (grain !== "hourly") return null
    const minutes = minutesDuPasCompteur ?? null
    if (!minutes || minutes <= 0 || minutes > 60) return null
    return Math.round(60 / minutes)
}

/** L'origine d'UN point, des seuls faits servis pour lui. */
function origineDuPoint(
    provenance: ProvenanceSeries | null | undefined,
    n: number | null | undefined,
    partiel: boolean | null | undefined,
    attendu: number | null | undefined
): OrigineSerie | null {
    if (!provenance) return null
    if (provenance === "raw") return "mesure"
    if (provenance === "corrected") return "correction"
    // `computed` et `migrated_v1` : ce n'est pas une mesure du compteur. Reste à
    // savoir si le calcul est CERTAIN, ce que disent le drapeau `partiel` servi
    // par le lecteur et, à défaut, le compte de points agrégés.
    if (partiel === true) return "estimation"
    if (
        typeof attendu === "number" &&
        attendu > 0 &&
        typeof n === "number" &&
        Number.isFinite(n) &&
        n < attendu
    ) {
        return "estimation"
    }
    return "calcul"
}

/**
 * La répartition des origines d'une série, lue de ses points.
 *
 * `n`, `partiel` et `attendu` sont tous FACULTATIFS : sans eux, un point calculé
 * reste « Calculé ». Ils ne servent qu'à reconnaître le calcul incomplet.
 */
export function origineDesPoints(
    t: Array<ProvenanceSeries | null | undefined> | null | undefined,
    n?: Array<number | null> | null,
    partiel?: Array<boolean | null> | null,
    attendu?: number | null
): RepartitionOrigine {
    const provenances = t ?? []
    const parPoint = provenances.map((provenance, i) =>
        origineDuPoint(provenance, n?.[i], partiel?.[i], attendu)
    )

    const comptes = new Map<OrigineSerie, number>()
    let pointsConnus = 0
    for (const origine of parPoint) {
        if (!origine) continue
        pointsConnus += 1
        comptes.set(origine, (comptes.get(origine) ?? 0) + 1)
    }

    const parts: PartOrigine[] = ORDRE_ORIGINES.filter((o) =>
        comptes.has(o)
    ).map((origine) => {
        const points = comptes.get(origine) ?? 0
        return {
            origine,
            points,
            pourcent: Math.round((points / pointsConnus) * 100),
        }
    })

    return {
        parPoint,
        pointsConnus,
        parts,
        homogene: parts.length === 1 ? parts[0].origine : null,
    }
}

/**
 * L'origine DOMINANTE d'une série : celle qui porte le plus de points, l'ordre
 * de lecture départageant une égalité. Elle ne remplace jamais les parts à
 * l'écran ; elle sert là où un seul mot est possible (une ligne, un repli).
 */
export function origineDominante(
    repartition: RepartitionOrigine
): OrigineSerie | null {
    let retenue: PartOrigine | null = null
    for (const part of repartition.parts) {
        if (!retenue || part.points > retenue.points) retenue = part
    }
    return retenue?.origine ?? null
}

/**
 * Le mot écrit sur la pastille. Une série homogène porte le mot seul ; une série
 * mixte porte le mot ET sa part, parce que « Mesuré » sur une série calculée à
 * 8 % serait une demi-vérité.
 */
export function libelleDeLaPart(
    part: PartOrigine,
    nombreDeParts: number
): string {
    const mot = LIBELLES_ORIGINE[part.origine]
    return nombreDeParts > 1 ? `${mot} ${part.pourcent} %` : mot
}

/**
 * L'origine d'une LIGNE du dépliant, qui peut regrouper plusieurs points.
 *
 * Quand les points regroupés ne s'accordent pas, c'est le MOINS certain qui est
 * écrit : une ligne dont un pas est estimé n'est pas une ligne mesurée. Dire
 * l'inverse serait présenter une extrapolation comme un relevé.
 */
export function origineDUnGroupe(
    origines: Array<OrigineSerie | null>
): OrigineSerie | null {
    let retenue: OrigineSerie | null = null
    for (const origine of origines) {
        if (!origine) continue
        if (
            retenue === null ||
            PRECEDENCE_GROUPE.indexOf(origine) >
                PRECEDENCE_GROUPE.indexOf(retenue)
        ) {
            retenue = origine
        }
    }
    return retenue
}

/**
 * Du plus certain au moins certain, pour départager une ligne regroupée. Une
 * valeur corrigée par le distributeur reste une valeur qu'il a établie : elle
 * passe avant un calcul, et loin devant une estimation.
 */
const PRECEDENCE_GROUPE: OrigineSerie[] = [
    "mesure",
    "correction",
    "calcul",
    "estimation",
]
